import { useCallback, useEffect, useMemo, useState } from "react";
import Joi from "joi-browser";

import { ConversationReminder, ConversationReminderMessageType } from "./types";

type ConversationReminderState = ConversationReminder & {
  errors: {
    delayInMinutes: null;
    staticMessage: null;
    dynamicPrompt: null;
  };
};

type UseConversationReturn = {
  state: ConversationReminderState;
  isError: boolean;
  toggleEnabled: () => void;
  updateDelayInMinutes: (delay: number) => void;
  updateMessageType: (type: ConversationReminderMessageType) => void;
  updateStaticMessage: (message: string) => void;
  updateDynamicPrompt: (prompt: string) => void;
};

export { ConversationReminderState };

export default useConversationReminderState;

const schema = {
  delayInMinutes: Joi.number()
    .required()
    .min(1)
    .max(1440)
    .label("Delay (mins)"),
  staticMessage: Joi.string().required().label("Message"),
  dynamicPrompt: Joi.string().required().label("Prompt"),
};

function validate(value, schema) {
  const { error } = schema.validate(value);

  if (error) {
    return { isError: true, errorMessage: error.details[0].message };
  }

  return { isError: false, errorMessage: undefined };
}

function getConversationReminderInitialState(conversationReminder) {
  return {
    enabled: conversationReminder.enabled,
    delayInMinutes: conversationReminder.delayInMinutes,
    messageType: conversationReminder.messageType,
    staticMessage: conversationReminder.staticMessage,
    dynamicPrompt: conversationReminder.dynamicPrompt,
    errors: {
      delayInMinutes: null,
      staticMessage: null,
      dynamicPrompt: null,
    },
  };
}

function useConversationReminderState(
  conversationReminder: ConversationReminder,
): UseConversationReturn {
  const [state, setState] = useState<ConversationReminderState>(() =>
    getConversationReminderInitialState(conversationReminder),
  );

  useEffect(() => {
    setState(getConversationReminderInitialState(conversationReminder));
  }, [conversationReminder]);

  const toggleEnabled = useCallback(() => {
    setState((prev) => {
      return {
        ...prev,
        ...getConversationReminderInitialState(conversationReminder),
        enabled: !prev.enabled,
      };
    });
  }, [conversationReminder]);

  const updateDelayInMinutes = useCallback((delay: number) => {
    const { isError, errorMessage } = validate(
      Number(delay),
      schema.delayInMinutes,
    );

    setState((prev) => {
      return {
        ...prev,
        delayInMinutes: Number(delay),
        errors: {
          ...prev.errors,
          delayInMinutes: isError ? errorMessage : null,
        },
      };
    });
  }, []);

  const updateMessageType = useCallback((messageType) => {
    setState((prev) => {
      return {
        ...prev,
        messageType,
        errors: {
          ...prev.errors,
          staticMessage: null,
          dynamicPrompt: null,
        },
      };
    });
  }, []);

  const updateStaticMessage = useCallback((message) => {
    const { isError, errorMessage } = validate(
      message,
      schema.staticMessage.trim(),
    );

    setState((prev) => {
      return {
        ...prev,
        staticMessage: message,
        errors: {
          ...prev.errors,
          staticMessage: isError ? errorMessage : null,
        },
      };
    });
  }, []);

  const updateDynamicPrompt = useCallback((prompt) => {
    const { isError, errorMessage } = validate(
      prompt,
      schema.dynamicPrompt.trim(),
    );

    setState((prev) => {
      return {
        ...prev,
        dynamicPrompt: prompt,
        errors: {
          ...prev.errors,
          dynamicPrompt: isError ? errorMessage : null,
        },
      };
    });
  }, []);

  const value = useMemo(() => {
    return {
      state,
      isError: Object.values(state.errors).some((val) => val),
      toggleEnabled,
      updateDelayInMinutes,
      updateMessageType,
      updateStaticMessage,
      updateDynamicPrompt,
    };
  }, [state]);

  return value;
}
